/**
 * QMP-FE-2 — STEP 2 "ITP Rules": 2a Preparing & endorsing · 2b Hold points ·
 * 2c Witness points.
 *
 * LAYOUT, COPY AND VALUES ARE THE MOCKUP'S (`mockup-qmp-v79-official.html`,
 * `#w2`, the v47 `rt()` renderer): one subarea at a time (D11.13), each a
 * first row "All packages" / "All other packages" plus the packages with a
 * row of their own ("Own rule" / "automatic · <reason>"). The grammar lives
 * in `lib/qmpRules`; the state in `useQmpRuleEditor`, held by the page.
 *
 * NO BUSINESS RULE IS REPEATED HERE beyond the grammar of the rows: a value
 * the server refuses is sent anyway and the refusal is shown on the row of
 * the package it names.
 *
 * QMP-FE-4: the table, the subarea card, the subnav and the "?" popover are
 * exported and driven by a configuration, so steps 4 and 5
 * (`NcrRfiStep`, `AiUsageStep`) declare only their columns and cells. They
 * draw the mockup's `table.endt.rt` (`look: 'endt'`), its `own rule` label and
 * its `<select>` footer; step 2 keeps `.rt2`, "Own rule" and the picker.
 */
import React from 'react';
import {
  lines, setGeneral, addOwnRule, removeOwnRule, setPackageRule,
  selectOptions, fieldOptions, samePreparedBy, positionsOf, POSITION_LABEL,
  formatResponseTime, formatIfOverdue,
  formatNotice, formatSignoffDue, formatDocRelease,
} from '../../lib/qmpRules';

// QMP-FE-7 — the titles of the subareas are the server's (`steps`, read by
// the editor as `editor.titles`), in the order of mockup `SUBS` (v79:679).
export const SUBS2 = ['itp_rules', 'hold_points', 'witness_points'];

// ---------------------------------------------------------------------------
// mockup tokens (`.rt2`, `.chip`, `select.control`, `.addsel`, `.excbar`)
// ---------------------------------------------------------------------------
const CONTROL = 'w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] '
  + 'pt-[8px] pb-[8px] pl-[8px] pr-[6px] text-[12px] disabled:opacity-60';
const TH = 'text-left text-[10.5px] tracking-[.08em] uppercase text-[#5b6b85] font-bold px-[10px] py-[10px] '
  + 'bg-[#0b1322] border-b border-[#16223a] whitespace-nowrap';
const TD = 'px-[10px] py-[9px] align-middle text-[12.5px] border-t border-[#16223a] overflow-hidden';
const TD_LBL = `${TD} font-semibold [&_b]:block [&_b]:leading-[1.3]`;
const TD_LAST = `${TD} text-center`;
const GEN_BAR = 'shadow-[inset_3px_0_0_#f97316]';
const BAD_BAR = 'shadow-[inset_3px_0_0_#f59e0b]';
const SUB2 = 'text-[11px] mt-[2px] font-normal';
const RWHY = 'text-[11px] text-[#94a3b8] mt-[4px] italic';
const RWARN = 'text-[11px] text-[#fbbf24] mt-[4px] leading-[1.4] font-normal';
const CHIPS = 'flex gap-[5px] flex-wrap items-center';
const CHIP = 'inline-flex items-center whitespace-nowrap rounded-full';
const CHIP_PARTY = `${CHIP} gap-[2px] text-[11.5px] font-medium pl-[9px] pr-[4px] py-[4px] border border-[#1c2941] bg-[#0b1322]`;
const CHIP_TBA = `${CHIP} gap-[6px] text-[10px] font-semibold px-[8px] py-[3px] text-[#fbbf24] border border-[rgba(245,158,11,.4)] bg-[rgba(245,158,11,.08)]`;
const CHIP_DIM = `${CHIP} gap-[6px] text-[10px] font-semibold px-[8px] py-[3px] text-[#5b6b85] border border-[#16223a] bg-transparent`;
const ADDSEL = 'w-[60px] min-w-0 appearance-none bg-[#0b1322] border border-dashed border-[#1c2941] rounded-[8px] '
  + 'py-[5px] px-0 text-center text-[11.5px] text-[#94a3b8] cursor-pointer disabled:opacity-60';
const X_BTN = 'text-[#5b6b85] hover:text-[#f4f7fc]';
const ERR = 'text-[12.5px] text-[#fca5a5]';
const SMALL_BTN = 'px-[12px] py-[6px] rounded-[8px] text-[12px] font-bold';
const Dash = () => <span className="text-[#5b6b85] pl-[6px]">—</span>;

const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

// ---------------------------------------------------------------------------
// tooltips (mockup `.tip` bodies, shown in the `.pop` popover)
// ---------------------------------------------------------------------------
const TIPS = {
  au: {
    title: 'ITP prepared by',
    body: <>Choose the party responsible for creating the ITPs.</>,
  },
  end: {
    title: 'Endorsed by',
    body: (
      <>
        Choose which parties must endorse the ITP before it can be used. If more than one is
        listed, <b>all of them must endorse</b>. Choose <b>Not required</b> where the company
        preparing the ITP can publish it without endorsement.
      </>
    ),
  },
  resp: {
    title: 'Response time',
    body: (
      <>
        How many working days each endorsing company has to respond after the ITP revision is
        submitted. It is a deadline to <b>respond</b>, not to agree: if no response is received by
        the deadline, it is recorded as overdue against that company. ConstructQA sends
        reminders. <b>Time never endorses an ITP automatically.</b>
      </>
    ),
  },
  over: {
    title: 'If overdue',
    body: (
      <>
        What happens when an endorsing company has not responded by the deadline. <b>Wait for
        endorsement:</b> no ITC can be created from that ITP revision until it is
        endorsed. <b>Proceed at risk:</b> the Executing Party may create ITCs from the unendorsed
        revision by an explicit, signed act; every ITC created this way is marked as issued at
        risk, and the endorsement stays outstanding. Nothing happens automatically.
      </>
    ),
  },
  hpNotice: {
    title: 'Booking notice',
    body: <>Minimum notice before the HP inspection to book the inspection and notify the required parties.</>,
  },
  hpDue: {
    title: 'Sign-off due (inspection)',
    body: (
      <>
        For hold points released by inspection: time allowed after the booked inspection time for
        the required parties to release or reject the hold point before it becomes overdue.
      </>
    ),
  },
  hpDocDue: {
    title: 'Release due (document)',
    body: (
      <>
        For hold points released by document: working days allowed after the evidence is
        submitted for the required parties to release or reject the hold point before it becomes
        overdue. No inspection is booked for a document hold. ITPs in these packages inherit this
        value; choose <b>Set in each ITP</b> to leave it to the ITP author.
      </>
    ),
  },
  wpNotice: {
    title: 'Notice',
    body: (
      <>
        Minimum notice (before the inspection window opens) for the Executing Party to notify the
        required parties that the work will be at that point. With <b>No minimum notice</b>,
        notice can be given at any time before the window opens.
      </>
    ),
  },
  wpDue: {
    title: 'Sign-off due',
    body: (
      <>
        Time allowed (after the inspection window closes) for the required parties to sign off the
        witness point, accepting or rejecting the work. If no sign-off is recorded by then, the
        witness point is recorded as not signed off in time. The ITC can continue without the
        witness point sign-off. The required parties can still sign off later, but the record
        shows the sign-off was late.
      </>
    ),
  },
};

/** The context a subarea is edited against (`punch_list` reads the handover
 *  path; every other subarea is `editor.ctx`). */
export const sectionCtx = (editor, section) => (
  editor.ctxOf ? editor.ctxOf(section) : editor.ctx);

// Mockup `.err` (v79:330), `.rwhy` (253), `.rwarn`, `.btn.sm` (422), `.x` (82).
export const T_ERR = ERR;
export const T_RWHY = RWHY;
export const T_RWARN = RWARN;
export const T_SMALL_BTN = SMALL_BTN;
export const T_X_BTN = X_BTN;
export const T_SUB2 = SUB2;
export const T_TH = TH;
export const T_BAD_BAR = BAD_BAR;
/** Mockup `.rt2.sum td` (v79:410-412). */
export const T_SUM_TD = 'px-[10px] py-[9px] align-middle text-[12px] text-[#94a3b8] '
  + 'leading-[1.45] border-t border-[#16223a] overflow-hidden';
/** Mockup `.rt2 .rp` and `.rt2 .miss` (v79:409). */
export const T_RP = 'text-[#f4f7fc]';
export const T_MISS = 'text-[#fbbf24]';
/** Mockup `.dimtxt` (v79:439). */
export const T_DIMTXT = 'text-[#5b6b85] text-[11.5px]';

export function QButton({ tipKey, onOpen, large = false }) {
  return (
    <button
      type="button"
      data-testid={`qr-tip-btn-${tipKey}`}
      onClick={(e) => onOpen(tipKey, e.currentTarget)}
      className={`ml-[5px] ${large ? 'w-[19px] h-[19px]' : 'w-[17px] h-[17px]'} rounded-full border border-[#1c2941] text-[#5b6b85] text-[10px] leading-none inline-flex items-center justify-center align-middle hover:text-[#f4f7fc] hover:border-[#2a3a5c]`}
    >
      ?
    </button>
  );
}

// Mockup `openPop`: below the button, kept in the viewport; a second click on
// the same "?" closes it; a click elsewhere or a scroll closes it.
export function useTip(tips = TIPS) {
  const [tip, setTip] = React.useState(null);
  const popRef = React.useRef(null);
  React.useEffect(() => {
    if (!tip) return undefined;
    const close = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-testid^="qr-tip-btn-"]')) return;
      setTip(null);
    };
    const onScroll = () => setTip(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll);
    };
  }, [tip]);
  const open = (key, btn) => {
    if (tip && tip.key === key) { setTip(null); return; }
    const r = btn.getBoundingClientRect();
    const W = window.innerWidth || 1440;
    const w = Math.min(380, W - 24);
    setTip({ key, left: Math.max(12, Math.min(r.left, W - w - 12)), top: r.bottom + 8 });
  };
  const data = tip ? tips[tip.key] : null;
  const pop = data ? (
    <div
      ref={popRef}
      role="tooltip"
      className="fixed z-[90] w-[380px] max-w-[calc(100vw-24px)] bg-[#101a2e] border border-[rgba(249,115,22,.55)] rounded-[10px] px-[16px] py-[14px] text-[12.5px] leading-[1.55] text-[#94a3b8] shadow-[0_12px_34px_rgba(0,0,0,.45)] [&_b]:text-[#f4f7fc]"
      style={{ left: tip.left, top: tip.top }}
      data-testid="qr-tip"
    >
      <span className="block text-[#f4f7fc] font-bold mb-[6px]">{data.title}</span>
      {data.body}
    </div>
  ) : null;
  return { open, pop };
}

// ---------------------------------------------------------------------------
// cells
// ---------------------------------------------------------------------------

// Mockup `select.control` outside `.rt2` (v79:74-75).
export const CONTROL_ENDT = 'w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] '
  + 'px-[10px] py-[8px] text-[12.5px] disabled:opacity-60';

/** A select of SERVED choices (`served`: `[{label, value}]`); `fallback`
 *  labels a current value that is not one of them. */
export function ValueSelect({
  served, current, fallback, same, onPick, disabled, testId, className = CONTROL,
}) {
  const { options, selected } = selectOptions(served, current, fallback, same);
  return (
    <select
      className={className}
      value={selected}
      disabled={disabled}
      onChange={(e) => {
        const o = options.find((x) => x.key === e.target.value);
        if (o) onPick(clone(o.value));
      }}
      data-testid={testId}
    >
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}

const partyName = (ref, names) => (ref && ref.position
  ? POSITION_LABEL[ref.position]
  : (ref && names[ref.company_id]) || '—');

// Mockup `authorCellQ`: EP / AP / "Executing or Acceptance Party" (the served
// choices); a package row offers only the choices whose parties it has.
function PreparedByCell({
  value, row, onChange, disabled, names, options, k,
}) {
  const have = row ? positionsOf(row) : null;
  const allowed = fieldOptions(options, 'itp_rules', 'prepared_by')
    .filter((o) => !have || (o.value || []).every((ref) => have.includes(ref.position)));
  const label = (refs) => (refs || []).map((r) => partyName(r, names)).join(' or ') || '—';
  return (
    <ValueSelect
      served={allowed}
      current={(value && value.prepared_by) || []}
      fallback={label}
      same={samePreparedBy}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, prepared_by: v })}
      testId={`qr-author-${k}`}
    />
  );
}

// Mockup `apprCell`: chips + "+ Add"; the first row names positions only, a
// package row its own positions and the project's live companies. The
// positions offered are the served choices, in the mockup's order (VP, AP, EP).
function EndorsedByCell({
  value, row, onChange, disabled, names, companies, options, k,
}) {
  const e = (value && value.endorsement) || {};
  const set = (patch) => onChange({ ...value, endorsement: { ...e, ...patch } });
  if (!e.required) {
    return (
      <div className={CHIPS} data-testid={`qr-endorse-${k}`}>
        <span className={`${CHIP_PARTY} text-[#94a3b8]`}>
          Not required
          {!disabled && (
            <button
              type="button"
              className={`${X_BTN} text-[12px] px-[3px]`}
              title="Require endorsement again"
              onClick={() => set({ required: true, endorsed_by: [] })}
              data-testid={`qr-nr-x-${k}`}
            >
              ✕
            </button>
          )}
        </span>
      </div>
    );
  }
  const list = e.endorsed_by || [];
  const has = (pred) => list.some(pred);
  const servedLabel = {};
  fieldOptions(options, 'itp_rules', 'endorsement.endorsed_by[]').forEach((o) => {
    if (o.value && o.value.position) servedLabel[o.value.position] = o.label;
  });
  const positions = ['verification', 'acceptance', 'execution']
    .filter((p) => servedLabel[p])
    .filter((p) => !row || positionsOf(row).includes(p))
    .filter((p) => !has((r) => r.position === p));
  const cos = row ? companies.filter((c) => !has((r) => r.company_id === c.id)) : [];
  const onAdd = (v) => {
    if (!v) return;
    if (v === '__nr__') { set({ required: false, endorsed_by: [] }); return; }
    const ref = v.startsWith('pos:') ? { position: v.slice(4) } : { company_id: v.slice(3) };
    set({ endorsed_by: list.concat([ref]) });
  };
  return (
    <div className={CHIPS} data-testid={`qr-endorse-${k}`}>
      {list.map((ref, i) => (
        <span key={ref.position || ref.company_id} className={`${CHIP_PARTY} text-[#f4f7fc]`}>
          {partyName(ref, names)}
          {!disabled && (
            <button
              type="button"
              className={`${X_BTN} text-[12px] px-[3px]`}
              title="Remove"
              onClick={() => set({ endorsed_by: list.filter((_, j) => j !== i) })}
              data-testid={`qr-endorse-x-${k}-${i}`}
            >
              ✕
            </button>
          )}
        </span>
      ))}
      <select
        className={ADDSEL}
        style={{ textAlignLast: 'center' }}
        value=""
        disabled={disabled}
        onChange={(ev) => onAdd(ev.target.value)}
        data-testid={`qr-endorse-add-${k}`}
      >
        <option value="">+ Add</option>
        <option value="__nr__">Not required</option>
        {(positions.length > 0 || cos.length > 0) && <option disabled>──────────</option>}
        {positions.map((p) => <option key={p} value={`pos:${p}`}>{servedLabel[p]}</option>)}
        {cos.map((c) => <option key={c.id} value={`co:${c.id}`}>{names[c.id]}</option>)}
      </select>
      {list.length === 0 && <span className={CHIP_TBA}>Add at least one</span>}
    </div>
  );
}

function ResponseTimeCell({
  value, onChange, disabled, options, k,
}) {
  const e = (value && value.endorsement) || {};
  if (!e.required) return <Dash />;
  return (
    <ValueSelect
      served={fieldOptions(options, 'itp_rules', 'endorsement.response_time')}
      current={e.response_time}
      fallback={formatResponseTime}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, endorsement: { ...e, response_time: v } })}
      testId={`qr-resp-${k}`}
    />
  );
}

function IfOverdueCell({
  value, onChange, disabled, options, k,
}) {
  const e = (value && value.endorsement) || {};
  if (!e.required) return <Dash />;
  if (e.response_time == null) {
    return <span className={CHIP_DIM} data-testid={`qr-over-${k}`}>Never overdue</span>;
  }
  return (
    <ValueSelect
      served={fieldOptions(options, 'itp_rules', 'endorsement.if_overdue')}
      current={e.if_overdue}
      fallback={formatIfOverdue}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, endorsement: { ...e, if_overdue: v } })}
      testId={`qr-over-${k}`}
    />
  );
}

// 2b — D14.2: booking notice, sign-off due, release due; no proceed-at-risk.
function HoldNoticeCell({
  value, onChange, disabled, options, k,
}) {
  return (
    <ValueSelect
      served={fieldOptions(options, 'hold_points', 'notice')}
      current={value && value.notice}
      fallback={formatNotice}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, notice: v })}
      testId={`qr-hnotice-${k}`}
    />
  );
}

function HoldSignoffCell({
  value, onChange, disabled, options, k,
}) {
  return (
    <ValueSelect
      served={fieldOptions(options, 'hold_points', 'signoff_due')}
      current={value && value.signoff_due}
      fallback={formatSignoffDue}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, signoff_due: v })}
      testId={`qr-hdue-${k}`}
    />
  );
}

// D13.3 — "Set in each ITP" is `document_release_due: null` with source "itp".
// One served select sets two fields (`sets`): the due, and its `source`.
function DocReleaseCell({
  value, onChange, disabled, options, k,
}) {
  const current = value ? {
    document_release_due: value.document_release_due ?? null,
    document_release_due_source: value.document_release_due_source,
  } : null;
  const served = fieldOptions(options, 'hold_points', 'document_release_due').map((o) => ({
    label: o.label,
    value: { document_release_due: o.value, document_release_due_source: o.source },
  }));
  return (
    <ValueSelect
      served={served}
      current={current}
      fallback={formatDocRelease}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, ...v })}
      testId={`qr-hdoc-${k}`}
    />
  );
}

// 2c — D14.3: notice before the window opens, sign-off due after it closes.
function WitnessNoticeCell({
  value, onChange, disabled, options, k,
}) {
  return (
    <ValueSelect
      served={fieldOptions(options, 'witness_points', 'notice')}
      current={value && value.notice}
      fallback={formatNotice}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, notice: v })}
      testId={`qr-wnotice-${k}`}
    />
  );
}

function WitnessSignoffCell({
  value, onChange, disabled, options, k,
}) {
  return (
    <ValueSelect
      served={fieldOptions(options, 'witness_points', 'signoff_due')}
      current={value && value.signoff_due}
      fallback={formatSignoffDue}
      disabled={disabled}
      onPick={(v) => onChange({ ...value, signoff_due: v })}
      testId={`qr-wdue-${k}`}
    />
  );
}

// ---------------------------------------------------------------------------
// the subareas
// ---------------------------------------------------------------------------

const SECTIONS = {
  itp_rules: {
    title: 'ITP preparation and endorsement',
    help: 'Choose who prepares each package\'s ITPs, who must endorse them, and how long they have to respond.',
    how: (
      <>
        The first row applies to every package. To give a package different rules, add it with a
        rule of its own; remove that row to send the package back to the first row. A package
        that lacks a party named in the first row gets an automatic row, adapted to the parties
        it has. When you save, every package keeps its own explicit rules — the first row is only
        a way of editing them together.
      </>
    ),
    columns: [
      { label: 'ITP prepared by', width: '21%', tip: 'au' },
      { label: 'Endorsed by', width: '25%', tip: 'end' },
      { label: 'Response time', width: '13%', tip: 'resp' },
      { label: 'If overdue', width: '15%', tip: 'over' },
    ],
    firstWidth: '22%',
    lastWidth: '3%',
    cells: [PreparedByCell, EndorsedByCell, ResponseTimeCell, IfOverdueCell],
  },
  hold_points: {
    title: 'Hold points',
    help: 'Choose the booking notice and when release is due.',
    how: (
      <>
        Set the project rules for hold points. Every package follows the first row unless it has
        a rule of its own below. Choose the minimum booking notice and when release is due — after
        the booked inspection for inspection holds, after the evidence is submitted for document
        holds. Work waits at a hold point until the required parties release it; time never
        releases a hold point. Each ITP then defines which line items are hold points, how each
        one is released (by inspection or by document) and who must sign them.
      </>
    ),
    columns: [
      { label: 'Booking notice', width: '17%', tip: 'hpNotice' },
      { label: 'Sign-off due (inspection)', width: '17%', tip: 'hpDue', wrap: true },
      { label: 'Release due (document)', width: '17%', tip: 'hpDocDue', wrap: true },
    ],
    firstWidth: '24%',
    lastWidth: '3%',
    cells: [HoldNoticeCell, HoldSignoffCell, DocReleaseCell],
  },
  witness_points: {
    title: 'Witness points',
    help: 'Choose the minimum notice before the inspection window opens and when sign-off is due after it closes.',
    how: (
      <>
        Set the project rules for witness points. Every package follows the first row unless it
        has a rule of its own below. A witness point is notified by inspection window, not by a
        booked time: the Executing Party gives notice of when the work will be at that point, and
        the required parties sign off, accepting or rejecting the work. The ITC can continue
        without the witness point sign-off. Each ITP then defines which line items are witness
        points and which parties must be notified.
      </>
    ),
    columns: [
      { label: 'Notice', width: '26%', tip: 'wpNotice' },
      { label: 'Sign-off due', width: '26%', tip: 'wpDue' },
    ],
    firstWidth: '34%',
    lastWidth: '4%',
    cells: [WitnessNoticeCell, WitnessSignoffCell],
  },
};

// Mockup `SIDE` (v79 lines 964-985): the cards beside each subarea.
const SIDE = {
  itp_rules: [
    {
      title: 'How endorsement works',
      icon: '⇄',
      body: (
        <>
          <p>An endorsing party responds in one of three ways:</p>
          <Resp tone="ok" title="Endorsed">Accepted as is. The revision can be used.</Resp>
          <Resp tone="warn" title="Endorsed with comments">
            Accepted. The revision can be used now, and the comments must be addressed in the
            next revision.
          </Resp>
          <Resp tone="bad" title="Returned — changes required">
            Not accepted; a reason is required. The ITP author revises and resubmits, and a new
            response period starts.
          </Resp>
        </>
      ),
    },
  ],
  hold_points: [
    {
      title: 'About hold points',
      icon: 'i',
      info: true,
      body: (
        <p>
          A hold point stops the ITC from progressing past that line until the required parties
          have released it. For a hold point released by inspection, the Executing Party books the
          inspection and notifies the required parties. For a hold point released by document, no
          inspection is booked: the Executing Party submits the evidence and the required parties
          release it in writing. Until every required release is recorded, the ITC cannot progress
          past the hold point.
        </p>
      ),
    },
    {
      title: 'Hold point outcomes',
      body: (
        <>
          <p>
            After the inspection (or the evidence) and up to the due time, the required parties
            respond. Three possible outcomes:
          </p>
          <Resp tone="ok" oic="✓" title="Released in time">
            Every required release is recorded by the due time. The ITC can then progress to the
            next line.
          </Resp>
          <Resp tone="bad" oic="✕" title="Rejected">
            The hold point has been rejected. The Executing Party must correct the issue and book a
            new inspection. The ITC <b>remains blocked</b> until the hold point is signed off.
          </Resp>
          <Resp tone="warn" oic="◷" title="No response">
            If the due time passes without a response, the hold point becomes <b>overdue</b>. Work
            waits: ConstructQA sends reminders and records the delay, and the line stays blocked
            until the required parties release it. <b>Time never releases a hold point.</b>
          </Resp>
        </>
      ),
    },
  ],
  witness_points: [
    {
      title: 'About witness points',
      icon: 'i',
      info: true,
      body: (
        <p>
          A witness point gives the required parties the opportunity to attend while the work is
          at that point. The Executing Party gives notice of the inspection window; the required
          parties sign off, accepting or rejecting the work. Unlike a hold point, the ITC can
          continue without the witness point sign-off — the record shows whether the sign-off was
          made in time.
        </p>
      ),
    },
    {
      title: 'Witness point outcomes',
      body: (
        <>
          <p>
            After the inspection window closes and up to the sign-off due time, the required
            parties sign off. Three possible outcomes:
          </p>
          <Resp tone="ok" oic="✓" title="Signed off in time">
            Every required sign-off is recorded by the due time, accepting the work.
          </Resp>
          <Resp tone="bad" oic="✕" title="Rejected">
            A required party signed off rejecting the work. The line follows the failed-inspection
            path: the Executing Party corrects the issue and raises a new window. Later items
            are <b>not blocked</b> by the witness point.
          </Resp>
          <Resp tone="warn" oic="◷" title="Not signed off in time">
            No sign-off recorded by the due time. The witness point is recorded as <b>not signed
            off in time</b>; the ITC continues. The required parties can still sign off later — the
            record shows it was late.
          </Resp>
        </>
      ),
    },
  ],
};

const RESP_TONE = {
  ok: ['border-[rgba(34,197,94,.35)] bg-[rgba(34,197,94,.06)]', 'text-[#86efac]'],
  warn: ['border-[rgba(245,158,11,.35)] bg-[rgba(245,158,11,.06)]', 'text-[#fbbf24]'],
  bad: ['border-[rgba(239,68,68,.35)] bg-[rgba(239,68,68,.06)]', 'text-[#fca5a5]'],
};
const OIC_TONE = {
  ok: 'bg-[rgba(34,197,94,.25)] text-[#86efac]',
  warn: 'bg-[rgba(245,158,11,.25)] text-[#fbbf24]',
  bad: 'bg-[rgba(239,68,68,.25)] text-[#fca5a5]',
};

/** Mockup `.resp` — an outcome box. `title` may be left out where the whole
 *  sentence is the body. */
export function Resp({ tone, title, oic, children }) {
  const [box, head] = RESP_TONE[tone];
  return (
    <div className={`border rounded-[8px] px-[12px] py-[9px] mt-[8px] ${box}`}>
      {title && (
      <span className={`block font-bold mb-[2px] ${head}`}>
        {oic && (
          <span className={`inline-flex w-[18px] h-[18px] rounded-full items-center justify-center text-[11px] mr-[8px] align-[-3px] font-extrabold ${OIC_TONE[tone]}`}>
            {oic}
          </span>
        )}
        {title}
      </span>
      )}
      {children}
    </div>
  );
}

/** Mockup `SIDE` / `renderSide`: the collapsible cards beside a subarea.
 *  `cards` lets another step bring its own (step 3). */
export function SideCards({ section, cards: given }) {
  const [closed, setClosed] = React.useState({});
  const cards = given || SIDE[section] || [];
  return (
    <aside
      className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-[14px] mt-[14px] min-[1400px]:block min-[1400px]:mt-0 min-[1400px]:sticky min-[1400px]:top-[20px]"
      data-testid="qr-side"
    >
      {cards.map((c, i) => {
        const key = `${section}|${i}`;
        const col = Boolean(closed[key]);
        return (
          <div
            key={key}
            className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[20px] py-[18px] mb-0 min-[1400px]:mb-[14px] text-[12.5px] text-[#94a3b8] leading-[1.55] [&_b]:text-[#f4f7fc] [&_p]:mb-[8px] [&_p:last-child]:mb-0"
            data-testid={`qr-side-card-${i}`}
          >
            <button
              type="button"
              className={`w-full flex items-center gap-[10px] text-[13.5px] font-bold text-[#f4f7fc] select-none text-left ${col ? '' : 'mb-[9px]'}`}
              onClick={() => setClosed((s) => ({ ...s, [key]: !s[key] }))}
              aria-expanded={!col}
            >
              {c.icon && (
                <span className={`w-[22px] h-[22px] rounded-[6px] inline-flex items-center justify-center text-[11px] font-extrabold border shrink-0 ${
                  c.info
                    ? 'text-[#93c5fd] border-[rgba(59,130,246,.45)] bg-[rgba(59,130,246,.12)]'
                    : 'text-[#94a3b8] border-[#1c2941] bg-[#0b1322]'
                }`}
                >
                  {c.icon}
                </span>
              )}
              {c.title}
              <span
                aria-hidden="true"
                className={`ml-auto text-[#5b6b85] text-[13px] transition-transform duration-150 ${col ? '-rotate-90' : ''}`}
              >
                ▾
              </span>
            </button>
            {!col && <div>{c.body}</div>}
          </div>
        );
      })}
    </aside>
  );
}

export function HowThisWorks({ children, testId }) {
  return (
    <details className="group -mt-[8px] mb-[16px] max-w-[760px]" data-testid={testId}>
      <summary className="cursor-pointer list-none inline-flex items-center gap-[6px] text-[12px] text-[#93c5fd] [&::-webkit-details-marker]:hidden">
        <span className="inline-flex w-[16px] h-[16px] rounded-full border border-[#3b82f6] items-center justify-center text-[10px]">?</span>
        How this works
        <span className="hidden group-open:inline text-[#94a3b8]">{' — hide'}</span>
      </summary>
      <div className="text-[#94a3b8] text-[12.5px] mt-[8px] leading-[1.5] [&_b]:text-[#f4f7fc]">
        {children}
      </div>
    </details>
  );
}

const EVERY_PACKAGE_OWN = 'Every package has a rule of its own. A new QWP starts on the standard rules.';

// Mockup `rt()` outside step 2 (v79:924): a `<select>` of the free packages.
function AddOwnRuleSelect({ section, free, onPick }) {
  if (!free.length) {
    return (
      <div className="flex items-center gap-[16px] flex-wrap mt-[12px]">
        <span className="text-[12px] text-[#5b6b85]" data-testid={`qr-excnote-${section}`}>{EVERY_PACKAGE_OWN}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-[16px] flex-wrap mt-[12px]">
      <select
        className="w-[300px] max-w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px]"
        value=""
        onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
        data-testid={`qr-add-select-${section}`}
      >
        <option value="">+ Add a package with its own rule…</option>
        {free.map((r) => <option key={r.qwp_id} value={r.qwp_id}>{r.name}</option>)}
      </select>
    </div>
  );
}

// Mockup `excBar` / `pickList`: the button becomes a package search in place.
function AddOwnRule({ section, free, total, onPick }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const ref = React.useRef(null);
  const input = React.useRef(null);
  React.useEffect(() => {
    if (open && input.current && input.current.focus) input.current.focus();
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    const esc = (e) => { if (e.key === 'Escape') { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const note = 'text-[12px] text-[#5b6b85]';
  if (!free.length) {
    return (
      <div className="flex items-start gap-[14px] mt-[12px] min-h-[36px]">
        <span className={note} data-testid={`qr-excnote-${section}`}>{EVERY_PACKAGE_OWN}</span>
      </div>
    );
  }
  if (!open) {
    const n = free.length;
    return (
      <div className="flex items-start gap-[14px] mt-[12px] min-h-[36px]">
        <button
          type="button"
          className="inline-flex items-center gap-[8px] border border-dashed border-[#1c2941] rounded-[8px] px-[14px] py-[8px] text-[12.5px] text-[#94a3b8] hover:text-[#f4f7fc] hover:border-[#2a3a5c]"
          onClick={() => setOpen(true)}
          data-testid={`qr-add-open-${section}`}
        >
          + Add a package with its own rule
        </button>
        <span className={note} data-testid={`qr-excnote-${section}`}>
          {n === total
            ? 'Every package follows the general rule.'
            : `${n} package${n === 1 ? '' : 's'} still follow${n === 1 ? 's' : ''} the general rule.`}
        </span>
      </div>
    );
  }
  const f = q.trim().toLowerCase();
  const rows = free.filter((r) => !f || (r.name || '').toLowerCase().includes(f));
  return (
    <div className="flex items-start gap-[14px] mt-[12px] min-h-[36px]">
      <div className="relative w-[360px] max-w-full" ref={ref}>
        <input
          ref={input}
          className="w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px] placeholder:text-[#3d4c68]"
          placeholder="Search packages…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid={`qr-add-search-${section}`}
        />
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[20] bg-[#101a2e] border border-[#1c2941] rounded-[9px] shadow-[0_12px_30px_rgba(0,0,0,.45)] overflow-hidden">
          {rows.length ? rows.map((r) => (
            <button
              key={r.qwp_id}
              type="button"
              className="block w-full text-left px-[12px] py-[9px] text-[12.5px] text-[#f4f7fc] hover:bg-[#0b1322]"
              onClick={() => { setOpen(false); setQ(''); onPick(r.qwp_id); }}
              data-testid={`qr-add-pick-${section}-${r.qwp_id}`}
            >
              {r.name}
            </button>
          )) : <div className="px-[12px] py-[9px] text-[12px] text-[#5b6b85]">No package matches.</div>}
        </div>
      </div>
      <span className={note}>Choose the package that needs a different rule. Esc cancels.</span>
    </div>
  );
}

const REVIEW_PARTY = 'This rule names a party the package does not have.';
const REVIEW_OTHER = 'This rule needs review.';
const PARTY_REASONS = ['position_not_in_package', 'company_not_in_project', 'party_not_in_package'];
/** What a `needs_review` item of this package says on its row. */
export const reviewText = (items) => ((items || []).some((n) => PARTY_REASONS.includes(n.reason))
  ? REVIEW_PARTY : REVIEW_OTHER);

// Mockup `table.endt.rt` (v79:108-114, 256, 297-299): each row a card.
const E_TABLE = 'w-full border-separate border-spacing-x-0 border-spacing-y-[8px]';
const E_TH = 'text-left text-[10.5px] tracking-[.08em] uppercase text-[#5b6b85] font-bold px-[10px] pt-0 pb-[2px] whitespace-nowrap';
const E_CELL = 'align-middle border-y border-[#16223a] text-[12.5px]';
const E_TD = `${E_CELL} bg-[#101a2e] p-[10px]`;
const E_TD_LBL = `${E_TD} border-l rounded-l-[9px] font-semibold w-[20%] [&_b]:block`;
const E_TD_LAST = `${E_TD} border-r rounded-r-[9px]`;
const E_PAIR = `${E_CELL} bg-[rgba(255,255,255,.015)] px-[10px] py-[7px]`;
const E_PAIR_LBL = `${E_PAIR} border-l rounded-l-[9px] font-normal`;
const E_PAIR_LAST = `${E_PAIR} border-r rounded-r-[9px]`;

/** The rows of a subarea. `conf` = columns, cells and look; `tid` names the
 *  table in test ids when a subarea draws two (4c); `notes` = draw the
 *  refusal and review lines (once per subarea). */
export function RuleTable({
  section, editor, openTip, conf: given, notes = true, tid,
}) {
  const conf = given || SECTIONS[section];
  const t = tid || section;
  const state = editor.states[section];
  if (!state || !conf) return null;
  const endt = conf.look === 'endt';
  const ctx = sectionCtx(editor, section);
  const { structure } = ctx;
  const l = lines(state, structure);
  const err = editor.errors[section];
  const review = editor.status[section].review;
  const disabled = !editor.canGovern || Boolean(editor.saving);
  const names = {};
  editor.companies.forEach((c) => { names[c.id] = c.name || c.short_name || ''; });
  structure.forEach((r) => {
    [r.executing, r.verification, r.acceptance].forEach((p) => {
      if (p && p.participation_id && !names[p.participation_id]) names[p.participation_id] = p.name || '';
    });
  });
  // `ctx` so a cell can read what its subarea is read against (3b needs the
  // handover path); every other cell ignores it.
  const cellProps = {
    disabled, names, companies: editor.companies, ctx, options: editor.options,
  };
  const upd = (fn) => editor.update(section, fn);
  const colCount = conf.columns.length + 2;
  const tdCls = endt ? E_TD : TD;
  const lblCls = endt ? E_TD_LBL : TD_LBL;
  const lastCls = endt ? E_TD_LAST : TD_LAST;
  const noteCell = endt ? 'px-[10px] pb-[4px]' : 'px-[10px] pb-[8px]';

  // Refusals and review marks of packages without a row of their own belong
  // to the first row.
  const ownIds = new Set(l.rows.map((r) => r.row.qwp_id));
  const firstNotes = [];
  if (notes && err && err.qwpId && !ownIds.has(err.qwpId)) {
    const r = structure.find((x) => x.qwp_id === err.qwpId);
    firstNotes.push({ key: 'err', cls: ERR, text: `${r ? r.name : err.qwpId}: ${err.message}`, testId: `qr-line-error-${section}-${err.qwpId}` });
  }
  if (notes) {
    l.free.filter((r) => review[r.qwp_id]).forEach((r) => {
      firstNotes.push({ key: r.qwp_id, cls: 'text-[11px] text-[#fbbf24]', text: `${r.name}: ${reviewText(review[r.qwp_id])}`, testId: `qr-review-${section}-${r.qwp_id}` });
    });
  }

  // Mockup `pairRow` (v79:915): "↳ <party>" lines under a row set per company.
  const pairRows = (value, row, k, onChange) => (conf.subRows
    ? conf.subRows({ ...cellProps, value, row, k, onChange }) : []).map((p) => (
      <tr key={`${k}|${p.key}`} data-testid={`qr-pair-${t}-${k}-${p.key}`}>
        <td className={E_PAIR_LBL}>
          <span className="text-[#94a3b8] text-[12px] pl-[14px] whitespace-nowrap">{`↳ ${p.label}`}</span>
        </td>
        {p.cells.map((c, i) => <td key={conf.columns[i].key || i} className={E_PAIR}>{c}</td>)}
        <td className={E_PAIR_LAST} />
      </tr>
  ));

  const table = (
    <table className={endt ? E_TABLE : 'w-full border-collapse table-fixed'} data-testid={`qr-table-${t}`}>
      <thead>
        <tr>
          <th className={endt ? `${E_TH} w-[20%]` : TH} style={endt ? undefined : { width: conf.firstWidth }}>Quality Work Package</th>
          {conf.columns.map((c, i) => (
            <th
              key={c.key || c.label || i}
              className={`${endt ? E_TH : TH} ${c.wrap ? '!whitespace-normal leading-[1.3]' : ''}`}
              style={{ width: c.width }}
            >
              {c.label}
              {c.tip && <QButton tipKey={c.tip} onOpen={openTip} />}
            </th>
          ))}
          <th className={endt ? E_TH : TH} style={{ width: conf.lastWidth }} aria-label="Remove" />
        </tr>
      </thead>
      <tbody className={endt ? '' : '[&>tr:first-child>td]:border-t-0'}>
        <tr data-testid={`qr-first-${t}`}>
          <td className={`${lblCls} ${endt ? '' : GEN_BAR}`}>
            <b data-testid={`qr-first-label-${t}`}>{l.first.label}</b>
            <div className={`${endt ? 'text-[11px] mt-[2px]' : SUB2} text-[#5b6b85]`} data-testid={`qr-first-sub-${t}`}>{l.first.sub}</div>
          </td>
          {conf.cells.map((Cell, i) => (
            <td key={conf.columns[i].key || conf.columns[i].label || i} className={tdCls}>
              <Cell
                {...cellProps}
                value={state.general}
                row={null}
                k="all"
                onChange={(v) => upd((st) => setGeneral(section, st, v, ctx))}
              />
            </td>
          ))}
          <td className={lastCls} />
        </tr>
        {pairRows(state.general, null, 'all', (v) => upd((st) => setGeneral(section, st, v, ctx)))}
        {firstNotes.length > 0 && (
          <tr>
            <td colSpan={colCount} className={noteCell}>
              {firstNotes.map((n) => <div key={n.key} className={n.cls} data-testid={n.testId}>{n.text}</div>)}
            </td>
          </tr>
        )}
        {l.rows.map(({ row, kind, why, value }) => {
          const id = row.qwp_id;
          const tba = row.executing && row.executing.appointed === false;
          const bad = Boolean(review[id]);
          const lineErr = notes && err && err.qwpId === id ? err.message : '';
          const onChange = (v) => upd((st) => setPackageRule(st, id, v));
          let tag = conf.ownLabel || (endt ? 'own rule' : 'Own rule');
          if (kind === 'auto') tag = <span className={RWHY}>{`automatic · ${why}`}</span>;
          return (
            <React.Fragment key={id}>
              <tr data-testid={`qr-line-${t}-${id}`}>
                <td className={`${lblCls} ${bad ? BAD_BAR : ''}`}>
                  <b>{row.name}</b>
                  <div
                    className={endt ? 'text-[11px] mt-[2px] text-[#5b6b85]' : `${SUB2} text-[#f97316]`}
                    data-testid={`qr-line-tag-${t}-${id}`}
                  >
                    {tag}
                    {tba ? ' · to be appointed' : ''}
                  </div>
                  {bad && notes && (
                    <div className={RWARN} data-testid={`qr-review-${t}-${id}`}>{reviewText(review[id])}</div>
                  )}
                </td>
                {conf.cells.map((Cell, i) => (
                  <td key={conf.columns[i].key || conf.columns[i].label || i} className={tdCls}>
                    <Cell {...cellProps} value={value} row={row} k={id} onChange={onChange} />
                  </td>
                ))}
                <td className={lastCls}>
                  {kind === 'own' && editor.canGovern && (
                    <button
                      type="button"
                      className={`${X_BTN} text-[15px] px-[4px] disabled:opacity-60`}
                      title="Remove — back to the first row"
                      disabled={disabled}
                      onClick={() => upd((st) => removeOwnRule(section, st, id, ctx))}
                      data-testid={`qr-remove-${t}-${id}`}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
              {pairRows(value, row, id, onChange)}
              {lineErr && (
                <tr>
                  <td colSpan={colCount} className={noteCell}>
                    <div className={ERR} data-testid={`qr-line-error-${section}-${id}`}>{lineErr}</div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
  if (endt) return table;
  return <div className="border border-[#16223a] rounded-[9px] overflow-hidden bg-[#101a2e]">{table}</div>;
}

/** Under the rows: add a package with its own rule; a refusal that names no
 *  package (with "Try again" on 503). */
export function RuleFoot({ section, editor, conf: given }) {
  const conf = given || SECTIONS[section];
  const state = editor.states[section];
  if (!state) return null;
  const { structure } = sectionCtx(editor, section);
  const l = lines(state, structure);
  const err = editor.errors[section];
  const upd = (fn) => editor.update(section, fn);
  const onPick = (id) => upd((st) => addOwnRule(st, id));
  return (
    <>
      {editor.canGovern && (conf && (conf.look === 'endt' || conf.foot === 'select') ? (
        <AddOwnRuleSelect section={section} free={l.free} onPick={onPick} />
      ) : (
        <AddOwnRule section={section} free={l.free} total={structure.length} onPick={onPick} />
      ))}
      {err && !err.qwpId && (
        <div className={`mt-[10px] ${ERR} flex items-center gap-[10px]`} data-testid={`qr-section-error-${section}`}>
          <span>{err.message}</span>
          {err.retry && (
            <button
              type="button"
              className={`${SMALL_BTN} border border-[#1c2941] text-[#94a3b8]`}
              disabled={Boolean(editor.saving)}
              onClick={() => editor.save(section)}
              data-testid={`qr-retry-${section}`}
            >
              Try again
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** A subarea: title, help, "How this works", "Restore standard rules" (the
 *  server writes the standard of this sub-step — QMP-FE-7), then its rows —
 *  `conf.render` when it draws more than one table (4c). */
export function SubCard({ section, editor, openTip, conf: given }) {
  const conf = given || SECTIONS[section];
  const [asking, setAsking] = React.useState(false);
  if (!conf) return null;
  return (
    <div data-testid={`qr-sub-${section}`}>
      <div className="flex items-start gap-[16px] mb-[12px]">
        <div className="flex-1">
          <div className="text-[17px] font-bold flex items-center gap-[10px] mb-[8px] flex-wrap">{conf.title}</div>
          <div className="text-[#94a3b8] text-[13px] mb-[16px] max-w-[760px]">
            {conf.help}
            {conf.helpTip && (
              <>
                {' '}
                <QButton tipKey={conf.helpTip} onOpen={openTip} large />
              </>
            )}
          </div>
          <HowThisWorks testId={`qr-how-${section}`}>{conf.how}</HowThisWorks>
        </div>
        {editor.canGovern && (
          <button
            type="button"
            className="text-[12px] text-[#5b6b85] underline decoration-dotted hover:text-[#f97316] whitespace-nowrap mt-[6px]"
            onClick={() => setAsking(true)}
            disabled={Boolean(editor.saving)}
            data-testid={`qr-restore-${section}`}
          >
            Restore standard rules
          </button>
        )}
      </div>
      {asking && (
        <div className="flex items-center gap-[10px] mb-[12px] text-[12.5px] text-[#94a3b8]" data-testid={`qr-restore-ask-${section}`}>
          <span>Restore ConstructQA&apos;s standard rules for this section, on every package?</span>
          <button
            type="button"
            className={`${SMALL_BTN} bg-[#f97316] text-[#180b02]`}
            disabled={Boolean(editor.saving)}
            onClick={() => {
              setAsking(false);
              editor.restore(section);
            }}
            data-testid={`qr-restore-yes-${section}`}
          >
            Restore
          </button>
          <button
            type="button"
            className={`${SMALL_BTN} border border-[#1c2941] text-[#94a3b8]`}
            onClick={() => setAsking(false)}
          >
            Cancel
          </button>
        </div>
      )}
      {conf.render ? conf.render({ section, editor, openTip }) : (conf.cells && (
        <>
          <RuleTable section={section} editor={editor} openTip={openTip} conf={conf} />
          <RuleFoot section={section} editor={editor} conf={conf} />
        </>
      ))}
    </div>
  );
}

// Mockup `#foot2sum` (render2aQ): the current rule of every package, with the
// companies that prepare and endorse its ITPs — the editor's state, live.
function Summary({ editor }) {
  const [q, setQ] = React.useState('');
  const state = editor.states.itp_rules;
  if (!state) return null;
  const { structure } = editor.ctx;
  const review = editor.status.itp_rules.review;
  const names = {};
  editor.companies.forEach((c) => { names[c.id] = c.name || c.short_name || ''; });
  const f = q.trim().toLowerCase();
  const rows = structure.filter((r) => !f || (r.name || '').toLowerCase().includes(f));
  const TDS = 'px-[10px] py-[9px] align-middle text-[12px] text-[#94a3b8] leading-[1.45] border-t border-[#16223a] overflow-hidden';
  const RP = 'text-[#f4f7fc]';
  const chain = (r) => [
    r.executing && r.executing.appointed !== false ? r.executing.name : 'To be appointed',
    r.verification && r.verification.name,
    r.acceptance && r.acceptance.name,
  ].filter(Boolean).join(' → ');
  const companyAt = (r, pos) => {
    if (pos === 'execution') return r.executing && r.executing.appointed !== false ? r.executing.name : 'to be appointed';
    const p = r[pos];
    return p ? p.name : null;
  };
  const one = (r, ref) => {
    if (!ref.position) return <span className={RP}>{names[ref.company_id] || '—'}</span>;
    const co = companyAt(r, ref.position);
    return (
      <>
        <span className={RP}>{POSITION_LABEL[ref.position]}</span>
        {' — '}
        {co || <span className="text-[#fbbf24]">none in this package</span>}
      </>
    );
  };
  const refs = (r, list) => (list || []).map((ref, i) => (
    <React.Fragment key={ref.position || ref.company_id}>
      {i > 0 && <br />}
      {one(r, ref)}
    </React.Fragment>
  ));
  return (
    <div className="col-span-full mt-[22px]" data-testid="qr-summary">
      <div className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px]">
        <div className="flex items-start gap-[16px] mb-[12px]">
          <div className="flex-1">
            <div className="text-[17px] font-bold flex items-center gap-[10px] mb-[8px] flex-wrap">How the rules apply to each package</div>
            <div className="text-[#94a3b8] text-[13px] mb-[16px] max-w-[760px]">
              Current rule per package, with the companies that prepare and endorse its ITPs.
            </div>
            <HowThisWorks testId="qr-how-summary">
              This table shows the current rule assigned to each package and the companies
              responsible for preparing and endorsing its ITPs.
            </HowThisWorks>
          </div>
          <input
            className="w-[220px] mt-[6px] shrink-0 bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px] placeholder:text-[#3d4c68]"
            placeholder="Search packages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="qr-sum-search"
          />
        </div>
        <div className="border border-[#16223a] rounded-[9px] overflow-hidden bg-[#101a2e]">
          <table className="w-full border-collapse table-fixed">
            <thead>
              <tr>
                <th className={TH} style={{ width: '19%' }}>Package</th>
                <th className={TH} style={{ width: '19%' }}>Rule</th>
                <th className={TH} style={{ width: '17%' }}>ITP prepared by</th>
                <th className={TH} style={{ width: '23%' }}>Endorsed by</th>
                <th className={TH} style={{ width: '11%' }}>Response time</th>
                <th className={TH} style={{ width: '11%' }}>If overdue</th>
              </tr>
            </thead>
            <tbody className="[&>tr:first-child>td]:border-t-0">
              {rows.length ? rows.map((r) => {
                const id = r.qwp_id;
                const line = state.rows[id] || { kind: 'follow' };
                const v = line.kind === 'follow' ? state.general : line.value;
                const e = (v && v.endorsement) || {};
                let rule = <span className={RP}>General rule</span>;
                if (line.kind === 'own') rule = <span className={RP}>Own rule</span>;
                if (line.kind === 'auto') rule = <><span className={RP}>Automatic</span>{` — ${line.why}`}</>;
                return (
                  <tr key={id} data-testid={`qr-sum-row-${id}`}>
                    <td className={`${TDS} !text-[#f4f7fc] font-semibold [&_b]:block [&_b]:leading-[1.3] ${review[id] ? BAD_BAR : ''}`}>
                      <b>{r.name}</b>
                      <div className={`${SUB2} text-[#5b6b85]`}>{chain(r)}</div>
                    </td>
                    <td className={TDS} data-testid={`qr-sum-rule-${id}`}>{rule}</td>
                    <td className={TDS} data-testid={`qr-sum-prep-${id}`}>{refs(r, v && v.prepared_by)}</td>
                    <td className={TDS} data-testid={`qr-sum-end-${id}`}>
                      {e.required ? refs(r, e.endorsed_by) : <span className={RP}>Not required</span>}
                    </td>
                    <td className={TDS}>{e.required ? formatResponseTime(e.response_time) : '—'}</td>
                    <td className={TDS}>
                      {e.required ? (e.response_time == null ? 'Never overdue' : formatIfOverdue(e.if_overdue)) : '—'}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className={`${TDS} !text-[#5b6b85] text-center`}>No package matches.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Mockup `.subtab .num/.tick/.dot`.
const ICON = 'w-[18px] h-[18px] rounded-full inline-flex items-center justify-center border';

export function Subnav({
  sub, onSub, status, subs = SUBS2, titles = {},
}) {
  return (
    <div className="flex gap-[8px] mb-[18px] flex-wrap" data-testid="qr-subnav">
      {subs.map((k, i) => {
        const st = status[k] || {};
        const on = sub === k;
        let icon;
        if (st.attn) {
          icon = (
            <span className={`${ICON} text-[10px] border-[rgba(245,158,11,.6)] text-[#fbbf24]`} title="A package is on a rule it cannot use">!</span>
          );
        } else if (st.dirty) {
          icon = (
            <span className={`${ICON} text-[10px] border-[rgba(245,158,11,.6)] text-[#fbbf24]`} title="Unsaved changes">●</span>
          );
        } else if (st.saved) {
          icon = (
            <span className={`${ICON} text-[10.5px] text-[#22c55e] border-[rgba(34,197,94,.5)]`} title="Saved">✓</span>
          );
        } else {
          icon = <span className={`${ICON} text-[10.5px] border-[#1c2941]`}>{i + 1}</span>;
        }
        return (
          <button
            key={k}
            type="button"
            onClick={() => onSub(k)}
            aria-current={on ? 'true' : undefined}
            data-testid={`qr-subtab-${k}`}
            className={`inline-flex items-center gap-[8px] border rounded-[9px] px-[14px] py-[8px] text-[12.5px] ${
              on
                ? 'border-[rgba(249,115,22,.55)] bg-[rgba(249,115,22,.12)] text-[#f4f7fc] font-bold'
                : 'border-[#1c2941] text-[#94a3b8] bg-transparent'
            }`}
          >
            {icon}
            {titles[k]}
          </button>
        );
      })}
    </div>
  );
}

/** The plan could not be read (e.g. 503 QMP_UNREADABLE): say so, offer to read again. */
export function LoadError({ editor }) {
  if (!editor.loadError) return null;
  return (
    <div className={`mb-[14px] ${ERR} flex items-center gap-[10px]`} data-testid="qr-load-error">
      <span>{editor.loadError}</span>
      <button
        type="button"
        className={`${SMALL_BTN} border border-[#1c2941] text-[#94a3b8]`}
        onClick={() => editor.reload()}
        data-testid="qr-load-retry"
      >
        Try again
      </button>
    </div>
  );
}

export default function ItpRulesStep({ editor, sub, onSub }) {
  const { open, pop } = useTip();
  return (
    <div
      className="grid grid-cols-1 min-[1400px]:grid-cols-[minmax(0,1fr)_280px] gap-x-[22px] items-start"
      data-testid="qs-pane-2"
    >
      <div className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] min-w-0">
        <Subnav sub={sub} onSub={onSub} status={editor.status} titles={editor.titles} />
        <LoadError editor={editor} />
        <SubCard key={sub} section={sub} editor={editor} openTip={open} />
      </div>
      <SideCards section={sub} />
      {sub === 'itp_rules' && <Summary editor={editor} />}
      {pop}
    </div>
  );
}
